import { action, Action, PayloadAction } from 'typesafe-actions';
import {
    WorkspaceOverlayConfiguration, WorkspacePreset, WorkspaceSchemeModel, WorkspaceTypeKeys,
} from './types';
import { isNullOrUndefined } from '../../util/utils';

export const workspaceSchemeLoaded = (workspaceScheme: WorkspaceSchemeModel): Action => action(
    WorkspaceTypeKeys.WORKSPACE_SCHEME_LOADED,
    workspaceScheme
);

export type FeatureStatusPayload = { featureId: string; status: string };
export type WorkspaceOverlayPayload = {
    workspaceOverlayName: string;
    workspaceOverlayConfiguration: WorkspaceOverlayConfiguration;
    targetNodeId: string;
};

export type PrintEventPayload = {
    contextObjectId: number;
    printStyle: string;
};

export type FeatureExpandTogglerPayload = { featureId: string; newValue: boolean };

export const workspaceSchemeError = (): Action => action(WorkspaceTypeKeys.WORKSPACE_SCHEME_ERROR);

export const loadWorkspacePreset = (workspacePreset: WorkspacePreset): PayloadAction<string, any> => action(
    WorkspaceTypeKeys.WORKSPACE_PRESET_LOADED, workspacePreset
);

export const setupGenericFeatureState = (featureId: string): PayloadAction<string, string> => action(
    WorkspaceTypeKeys.SETUP_GENERIC_FEATURES_STATE, featureId
);

export const toggleHeader = (featureId: string): PayloadAction<string, string> => action(WorkspaceTypeKeys.TOGGLE_HEADER, featureId);

export const toggleExpand = (featureId: string, newValue?: boolean): PayloadAction<string, FeatureExpandTogglerPayload> => {
    let payload: any = featureId;
    if (!isNullOrUndefined(newValue)) {
        payload = { featureId, newValue };
    }
    return action(WorkspaceTypeKeys.TOGGLE_EXPAND, payload);
};

export const setFeatureLoading = (featureId: string | string[]):
    PayloadAction<string, string | string[]> => action(WorkspaceTypeKeys.SET_FEATURE_LOADING, featureId);

export const stopFeatureLoading = (featureId: string): PayloadAction<string, string> => action(WorkspaceTypeKeys.STOP_FEATURE_LOADING, featureId);

export const toggleFocusMode = (): Action => action(WorkspaceTypeKeys.TOGGLE_FOCUS_MODE);

export const toggleFullScreenMode = (): Action => action(WorkspaceTypeKeys.TOGGLE_FULL_SCREEN);

export const setFeatureStatusIndicator = (featureId: string, status = ''): PayloadAction<string, FeatureStatusPayload> => {
    return action(WorkspaceTypeKeys.SET_FEATURE_STATUS_INDICATOR, { featureId, status });
};

export const setContextRequiredProperties = (properties: string[]): PayloadAction<string, string[]> => action(
    WorkspaceTypeKeys.SET_CONTEXT_REQUIRED_PROPERTIES,
    properties
);

export const resetContextRequiredProperties = (): Action => action(WorkspaceTypeKeys.RESET_CONTEXT_REQUIRED_PROPERTIES);

export const featureLoaded = (featureId: string): PayloadAction<string, string> => action(WorkspaceTypeKeys.FEATURE_LOADED, featureId);

export const featuresDoneSetup = (): Action => action(WorkspaceTypeKeys.FEATURES_SETUP_DONE);

export const setContextMenuVisibility = (featureId: string): PayloadAction<string, string> => action(
    WorkspaceTypeKeys.SET_CONTEXT_MENU_VISIBILITY, featureId
);

export const setActiveOverlay = (featureId: string): PayloadAction<string, string> => action(WorkspaceTypeKeys.SET_ACTIVE_OVERLAY, featureId);

export const resetActiveOverlay = (): Action => action(WorkspaceTypeKeys.RESET_ACTIVE_OVERLAY);

export const setPrintMode = (status: boolean): PayloadAction<string, boolean> => action(WorkspaceTypeKeys.SET_PRINT_MODE, status);

export const setWorkspaceOverlay = (
    name: string, targetNodeId: string, configuration: WorkspaceOverlayConfiguration
): PayloadAction<string, WorkspaceOverlayPayload> => {
    return action(
        WorkspaceTypeKeys.SET_WORKSPACE_OVERLAY,
        { workspaceOverlayName: name, workspaceOverlayConfiguration: configuration, targetNodeId }
    );
};

export const setClosedClicked = (payload: boolean) => ({
    type: WorkspaceTypeKeys.IS_CLOSED_CLICKED,
    payload,
});
export const setHasBeenEdited = (payload: boolean) => ({
    type: WorkspaceTypeKeys.HAS_BEEN_EDITED,
    payload,
});

export const closeWorkspaceOverlay = (): Action => {
    return action(WorkspaceTypeKeys.CLOSE_WORKSPACE_OVERLAY);
};

export const toggleSideBar = (expanded: boolean): Action => action(WorkspaceTypeKeys.TOGGLE_SIDE_BAR, expanded);

export const toggleStructureEditorCollapse = (expanded: boolean): Action => action(WorkspaceTypeKeys.TOGGLE_STRUCTURE_EDITOR, expanded);

export type PreviewInSplitViewAction = {
    featureId: string;
    type: WorkspaceTypeKeys.OPEN_CURRENT_CONTEXT_IN_SPLIT;
}

export const previewInSplitView = (featureId: string): PreviewInSplitViewAction => ({
    featureId,
    type: WorkspaceTypeKeys.OPEN_CURRENT_CONTEXT_IN_SPLIT,
});

export const setSplitViewItem = (item: any): Action => {
    return action(WorkspaceTypeKeys.OPEN_ITEM_IN_SPLIT_VIEW, item);
};

export const createPrintEventMessage = (contextObjectId: number, printStyle: string): PayloadAction<string, PrintEventPayload> => action(
    WorkspaceTypeKeys.CREATE_PRINT_EVENT_MESSAGE,
    { contextObjectId, printStyle }
);
